import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, Modal } from 'react-bootstrap';
import { CastType, IQuery, IWhere, IQuerySelectData, IUploadFilePreviewItem } from '../solution/Interfaces';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateFull } from '../solution/utils/AppUtil';
import { ScanLine, Check, FileText, UserCheck, X, Pencil, ThumbsUp, ArrowUp, Printer, Save } from 'lucide-react';
import ShowErrorAlert from '../ShowErrorAlert';
import Logo from "../../assets/img/logo.png";
import bkLogo from "../../assets/img/birkenstock_logo.png";
import hdLogo from "../../assets/img/hey_dude.png";
import tbLogo from "../../assets/img/ted_baker.png";
import dcLogo from "../../assets/img/digicape_logo_black.png";
import htLogo from "../../assets/img/hi-tec.svg"; 
import crLogo from "../../assets/img/crocs_logo_new.png";
import okLogo from "../../assets/img/oakley_logo.jpeg";


interface IStore {
    id: string;
    code: string;
    name: string;
    email: string
    customer_code: string;
    costing_code: string;
    sit_location: string;
    shopify_location_id: string;
    created: string;
    updated: string| null;
}

interface IComponent {
    store: IStore;
    close: () => void;
}

interface IStockReturn {
     id: string;
     created: string;
     created_by: string;
     updated: string | null;
     approved_by: string | null;
     doc_num: string;
     from_whs: string;
     notes: string | null;
     status: string;
     to_whs: string;
     total_qty_approved: number | null;
     total_qty_requested: number | null;
}

interface IStockReturnLine {
    id: string;
    created: string;
    updated: string | null;
    line_num: number;
    item_name: string;
    item_code: string;
    item_barcode: string;
    doc_num: string;
    qty_on_hand: number | null;
    qty_approved: number | null;
    qty_requested: number | null;
}

interface ISelectedScannedItem {
  id: string;
  barcode: string;
  item_code: string;
  item_group: string;
  name: string;
  product_code: string;
}

export const StoreReturn:React.FC<IComponent | null> = (props) => {

  const { fetchData, userDetail, responseErrorMessage, showErrorAlert, responseStatus }  = useAuth();
  const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
  const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
  const fetchStockReturnActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STORE-RETURN-ACTIONS/run`; 
  const fetchStockReturnLineActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STORE-RETURN-LINE-ACTIONS/run`; 
  const fetchStockTakeFilePreviewQuertUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/SCAN-FILE-SUMMARY/run2`;
  

  const [stockReturnState, setStockReturnState] = useState<IStockReturn[]>([]);
  const [stockReturnLineState, setStockReturnLineState] = useState<IStockReturnLine[]>([]);
  const [selectedStockReturn, setSelectedStockReturn] = useState<IStockReturn | null>(null);
  const [selectedStockReturnDocNum, setSelectedStockReturnDocNum] = useState<string>("");
  const [selectedStockReturnDocNumNotes, setSelectedStockReturnDocNumNotes] = useState<string>("");
  const [stockReturnNotes, setStockReturnNotes] = useState<string>("");
  const [approvedQty, setApprovedQty] = useState<{ [key: string]: number }>({});

  const [showCreatStockReturnModal, setShowCreatStockReturnModal] = useState<boolean>(false);
  const [showPendingStoreReturnModal, setShowPendingStoreReturnModal] = useState<boolean>(false);
  const [showDeletingStoreReturnModal, setShowDeletingStoreReturnModal] = useState<boolean>(false);
  const [showEditDocNumNotesModal, setShowEditDocNumNotesModal] = useState<boolean>(false);
  const [showApprovedStockReturnModal, setShowApprovedStockReturnModal] = useState<boolean>(false);

  const [submitRefreshData, setSubmitRefreshData] = useState<boolean>(false); 
  const [submitRefreshLineData, setSubmitRefreshLineData] = useState<boolean>(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [viewMode, setViewMode] = useState<'manager' | 'scan' | 'view' | 'print' | 'default'>('default'); 

  const [loading, setLoading] = useState<boolean>(false);

  //refs 
  const fileRef = useRef<HTMLInputElement | null>(null);
  const scanRef = useRef<HTMLInputElement | null>(null);
  const docNumNotesRef = useRef<HTMLTextAreaElement | null>(null);

  const [selectedScannedItem, setSelectedScannedItem] = useState<ISelectedScannedItem | null>(null);  
  const [showCustomQuantitySection, setShowCustomQuantitySection] = useState<boolean>(false);
  const [customQuantity, setCustomQuantity] = useState<string>('1');

  const [printTarget, setPrintTarget] = useState<'label' | 'manifest'>('label'); 

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editedQty, setEditedQty] = useState<Record<string, number>>({}); 
  const [inputDraft, setInputDraft] = useState<Record<string, string>>({});

  const [previewFileUpload, setPreviewFileUpload] = useState<boolean>(false);
  const [previewUploadedFileData, setPreviewUploadedFileData] = useState<IUploadFilePreviewItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewFailed, setPreviewFailed] = useState<boolean>(false);

  const defaultWhere: IWhere[] = [
    {
        fullColumnName: "from_whs",
        value: props?.store.code,
        operator: "EQ",
        cast: CastType.STRING
    },                   
  ];

  const defaultQueryState: IQuerySelectData = {
    appCode: "ares",
    selectCode: "scan-item",  
    params: {}
  };   

  const [querySelectState, setQuerySelectState] = useState<IQuerySelectData>(defaultQueryState);

  const [queryState, setQueryState] = useState<IQuery>({
    tableName: "sa_ares_store_return",
    select: [{
      fullColumnName: "id",
      aggregate: ""
    },
    {
      fullColumnName: "created",
      aggregate: ""
    },
    {
      fullColumnName: "created_by",
      aggregate: ""
    },
    {
      fullColumnName: "updated",
      aggregate: ""
    },
    {
      fullColumnName: "approved_by",
      aggregate: ""
    },
    {
      fullColumnName: "doc_num",
      aggregate: ""
    },
    {
      fullColumnName: "from_whs",
      aggregate: ""
    },
    {
      fullColumnName: "notes",
      aggregate: ""
    },
    {
      fullColumnName: "status",
      aggregate: ""
    },
    {
      fullColumnName: "to_whs",
      aggregate: ""
    },
    {
      fullColumnName: "lines.qty_approved",
      aggregate: "SUM",
    },
    {
      fullColumnName: "lines.qty_requested",
      aggregate: "SUM",
    },
   ],
    groupBy: ["id"],
    join: [
      {
        relationship: "lines",
        joinType: "LEFT"
      }
    ],
    orderBy: [ 
        {
            fullColumnName: "created",
            direction: "ASC",
            cast: CastType.TIMESTAMP
        }
    ],
    where: defaultWhere,
    mainTableOnly: false,
    includeExtraColumns: true,
    includeControlColumns:false,
    modifiedAfter: null,
    touchedBefore: null,
    isDirty: null,
    pageNum: 0,
    pageSize: 100,
    frontEndOnly: null
  });

   //Query Line State
 const [queryLineState, setQueryLineState] = useState<IQuery>({
    tableName: "sa_ares_store_return_line",
    select: [],
    groupBy: [],
    join: [{
      relationship: 'item',
      joinType: "LEFT"
    }],
    orderBy: [ 
        {
            fullColumnName: "created",
            direction: "ASC",
            cast: CastType.TIMESTAMP
        }
    ],
    where:[],
    mainTableOnly: false,
    includeExtraColumns: true,
    includeControlColumns: true,
    modifiedAfter: null,
    touchedBefore: null,
    isDirty: null,
    // pageNum: 0,
    // pageSize: 100,
    frontEndOnly: null
 });

 
  //useeffects
  useEffect(() => {
    setLoading(true); 
    fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processData, cleanUpLoader);
  }, [queryState, submitRefreshData]);

  useEffect(() => {
    if (showCreatStockReturnModal && fileRef.current) {
        fileRef.current.focus();
    }
  }, [showCreatStockReturnModal]);

  useEffect(() => {
    if (showEditDocNumNotesModal && docNumNotesRef.current) {
        docNumNotesRef.current.focus();
    }
  }, [showEditDocNumNotesModal]);

  useEffect(() => {
    if (viewMode === "scan" && scanRef.current) {
        scanRef.current.focus();
    }
  }, [viewMode]);

  useEffect(() => {
    const queryLine = {...queryLineState}
    queryLine.where = [
         {
            fullColumnName: "doc_num",
            value: selectedStockReturn?.doc_num, 
            operator: "EQ",
            cast: CastType.STRING
        }       
    ]
    setLoading(true); 
    fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryLine)}, processLineData, cleanUpLoader);
  }, [selectedStockReturn, submitRefreshLineData]);

  useEffect(() => {
    const initialApprovedQty: Record<string, number> = {};
  
    stockReturnLineState.forEach((line) => {
      const maxQty = line.qty_on_hand ?? 0;
      const requestedQty = line.qty_requested ?? 0;
      initialApprovedQty[line.item_code] = Math.min(requestedQty, maxQty);
    });
  
    setApprovedQty(initialApprovedQty);
  }, [stockReturnLineState]);

  useEffect(() => {
      if(previewFileUpload && (responseStatus === "400" || responseStatus === "404")){
          setPreviewUploadedFileData([]);
          setPreviewLoading(false);
          setPreviewFailed(true);
      }
  }, [responseStatus, previewFileUpload])


  const processData = (result: any) => {

    const stockReturn: IStockReturn[] = [];

    for (const key in result?.data) {

      const entry = result.data[key];
      const control = entry.control || {};
      const data = entry.data || {};

       // Access flat keys with dot-notation in string
      const total_qty_requested = data["lines.qty_requested"] ?? null;
      const total_qty_approved = data["lines.qty_approved"] ?? null;
  
      stockReturn.push({
        id: control.id,
        created: control.created,
        created_by: control.created_by,
        updated: control.updated ?? null,
        approved_by: data.approved_by ?? null,
        doc_num: data.doc_num,
        from_whs: data.from_whs,
        to_whs: data.to_whs,
        status: data.status,
        notes: data.notes ?? null,
        total_qty_requested,
        total_qty_approved,
      });
    }
  
    // Sort by created date descending
    stockReturn.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
  
    setStockReturnState(stockReturn);
  }


  const processLineData = (result: any) => {

    const stockReturnLines: IStockReturnLine[] = [];
  
    for(const key in result?.data){
  
        const entry = result.data[key];
        const control = entry.control || {};
        const data = entry.data || {};

        stockReturnLines.push({
            id: control.id,
            created: control.created,
            updated: control.updated ?? null,
            line_num: data.line_num,
            item_name: data["item.name"],
            item_code: data.item_code,
            item_barcode: data["item.barcode"], 
            doc_num: data.doc_num,
            qty_on_hand: data.qty_on_hand,
            qty_approved: data.qty_approved,
            qty_requested: data.qty_requested,
          });
    }

    setStockReturnLineState(stockReturnLines);
  };


  //default clean up loader
  const cleanUpLoader = () => {
     setLoading(false);
  }
    

   // Handle file/folder selection
  const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
    
      if (file) {
        const isXlsx = file.name.toLowerCase().endsWith(".xlsx");
    
        if (isXlsx) {
          setSelectedFile(file); 
        } else {
          setSelectedFile(null); 
        }
      } else {
        setSelectedFile(null); 
      }
  };

  // Handle Create Stock Return
  const handleCreateStockReturnSubmit = () => {
    if (props?.store?.code && !selectedFile) {
        setLoading(true);
        fetchData(fetchStockReturnActionsQueryUrl, 
          {method:"POST", body: JSON.stringify({ store_code: props?.store?.code, notes: stockReturnNotes })}, 
          (result: any, status: string) =>  { 
              if(status === "200"){
                setSelectedStockReturn(result);
                setViewMode("scan");
              }
          }, cleanUpLoader);

    }else if (props?.store?.code && selectedFile) {

         // how to do a multipe part form posts javascript  
         const formData = new FormData();
         formData.append('file', selectedFile);
         formData.append('j1', new Blob([JSON.stringify({ store_code: props?.store.code, notes: stockReturnNotes })], { type: 'application/json' }));

         const headers = new Headers();
         headers.append("Content-Type", "multipart/formdata");

         setLoading(true);
         fetchData(fetchStockReturnActionsQueryUrl, 
          {method:"POST", body: formData, headers: headers }, 
          (result: any, status: string) =>  { 
              if(status === "200"){
                // Causes refresh on data  
                setSubmitRefreshData(prev => !prev);
              }
          }, cleanUpLoader);
        
    }else{
        return; // Prevent further execution if the code is not available
    }

     // Reset fileRef after submission
     setSelectedFile(null);
      if (fileRef.current) {
          fileRef.current.value = ''; 
      }

     setStockReturnNotes("");
     setPreviewFileUpload(false);
     setPreviewUploadedFileData([]);
     setPreviewFailed(false);
  }

   //handle view button when clicked
   const handleViewClick = (storeReturn : IStockReturn) => {
     setViewMode('view'); // Switch to view mode
     setSelectedStockReturn(storeReturn);
   }

   //handle scan button when clicked
   const handleScanClick = (storeReturn : IStockReturn) => {
        setViewMode('scan'); // Switch to Scan mode
        setSelectedStockReturn(storeReturn);
   }

    // Add the event handler for "Enter" key press
    const handleScanKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, docNum: string) => {
        if (event.key === 'Enter') {
            event.preventDefault();  // Prevent default behavior (if needed)
            handleScanSubmit(docNum); 
        }
    };


    const handleScanSubmit = (docNum: string) => {
        setLoading(true);
        const scanValueItemCode = scanRef.current?.value.trim();
        if (!docNum || !scanValueItemCode) return;

        const queryPayload = {
          ...querySelectState,
          params: { ...querySelectState.params, scan_code: scanValueItemCode },
        };

        let shouldRunLineFetch = true;

        fetchData( queryUrlSelectNoCount,
          { method: "POST", body: JSON.stringify(queryPayload)},
          (result: any, status: string) => {
            if (status === "200") {
              const item = result?.data?.[0]?.data;
              const id = result?.data?.[0]?.id;

              if (item && id) {

                const itemGroup = item.item_group;

                const scannedItem: ISelectedScannedItem = {
                  id,
                  barcode: item.barcode,
                  item_code: item.item_code,
                  item_group: itemGroup,
                  name: item.name,
                  product_code: item.product_code,
                };

                setSelectedScannedItem(scannedItem);

                if (itemGroup === "Jibbitz") {
                  setShowCustomQuantitySection(true);
                  shouldRunLineFetch = false; // Don't run line fetch for Jibbitz
                }
              }
            }

            // Only run line fetch if allowed
            if (shouldRunLineFetch) {
              fetchData(fetchStockReturnLineActionsQueryUrl,
                { method: "POST", body: JSON.stringify({ doc_num: docNum, item_code: scanValueItemCode, qty_requested: 1 })},
                (result: any, status: string) => {
                  if (status === "200") {
                    setSubmitRefreshLineData((prev) => !prev);
                  }
                },
                cleanUpLoader
              );
            } 
            
            else {
              cleanUpLoader(); // still clean up if skipping line fetch
            }
          }
        );

        if (scanRef.current) {
          scanRef.current.value = "";
        }
    };

    
    const handleQuantitySubmit = () => {

      if (!selectedScannedItem || !selectedStockReturn?.doc_num) return;

      const { item_code, barcode } = selectedScannedItem;

      const parsedQty = Math.max(1, parseInt(customQuantity) || 1);

      const payload = {
        doc_num: selectedStockReturn.doc_num,
        qty_requested: parsedQty,
        item_code: item_code || barcode,
      };

      setLoading(true);

       fetchData(fetchStockReturnLineActionsQueryUrl,
        { method:"POST", body: JSON.stringify(payload)}, 
        (result: any, status: string) =>  { 
          if(status === "200"){
              setShowCustomQuantitySection(false);
              setSelectedScannedItem(null);
              setCustomQuantity('1');
              setSubmitRefreshLineData(prev => !prev); 
          }
        }, cleanUpLoader);
    };


   //handle pending stock return button when clicked
   const handlePendingStockReturnClick = (storeReturn : IStockReturn) => {
      setViewMode('default'); // Switch to default mode
      setShowPendingStoreReturnModal(true);
      setSelectedStockReturnDocNum(storeReturn.doc_num);
   }

   //handle the submit for setting pending store return
   const handlePendingStockReturnSubmit = () => {
       if(selectedStockReturnDocNum !== ""){
          setLoading(true);
          fetchData(fetchStockReturnActionsQueryUrl, 
            {method:"POST", body: JSON.stringify({ doc_num: selectedStockReturnDocNum,  status: "PENDING" })}, 
            (result: any, status: string) =>  {
                if(status === "200"){
                   // Causes refresh on data  
                   setSubmitRefreshData(prev => !prev);
                }
            }, cleanUpLoader);
            setSelectedStockReturnDocNum("");
       }else{
         return; 
       }
   }


  //handle deleting stock return button when clicked
  const handleDeletingStockReturnClick = (storeReturn : IStockReturn) => {
    setViewMode('default'); // Switch to default mode
    setShowDeletingStoreReturnModal(true);
    setSelectedStockReturnDocNum(storeReturn.doc_num);
  }
  
    //handle the submit for setting deleted store return
    const handleDeleteStockReturnSubmit = () => {
        if(selectedStockReturnDocNum !== ""){
           setLoading(true);
           fetchData(fetchStockReturnActionsQueryUrl, 
             {method:"POST", body: JSON.stringify({ doc_num: selectedStockReturnDocNum,  status: "DELETED"})}, 
             (result: any, status: string) =>  { 
                 if(status === "200"){
                   // Causes refresh on data  
                   setSubmitRefreshData(prev => !prev);
                 }
             }, cleanUpLoader);
             setSelectedStockReturnDocNum("");
        }else{
          return; 
        }
    }


  //handle manager approval view button when clicked
   const handleManagerApprovalViewClick = (storeReturn : IStockReturn) => {
    setViewMode('manager'); // Switch to manager mode
    setSelectedStockReturn(storeReturn);
  }

  const handlePrintStockReturnClick = (storeReturn : IStockReturn) => {
    setViewMode('print'); // Switch to print mode
    setSelectedStockReturn(storeReturn);
  }


  //handle open edit selectd doc num notes 
  const handleOpenEditDocNumNotesClick = (stockReturn: IStockReturn) => {
    setShowEditDocNumNotesModal(true); // open modal 
    setSelectedStockReturnDocNumNotes(stockReturn?.notes ?? ""); //set the notes
    setSelectedStockReturnDocNum(stockReturn?.doc_num); //set the doc num
  }

  const handleUpdateDocNumNotesSubmit = () => {
     if(selectedStockReturnDocNum !== ""){
        fetchData(fetchStockReturnActionsQueryUrl, 
          {method:"POST", body: JSON.stringify({ doc_num: selectedStockReturnDocNum, notes: selectedStockReturnDocNumNotes })}, 
          (result: any, status: string) =>  { 
              if(status === "200"){

                // Shadow copy and update notes
                if (selectedStockReturn && selectedStockReturn.doc_num === selectedStockReturnDocNum) {
                    setSelectedStockReturn({
                    ...selectedStockReturn,
                    notes: selectedStockReturnDocNumNotes,
                    });
                } 

                setSelectedStockReturnDocNumNotes(""); //set the notes
                setSelectedStockReturnDocNum(""); //set the doc num
              }else{
                setSelectedStockReturnDocNumNotes(""); //set the notes
                setSelectedStockReturnDocNum(""); //set the doc num
              }
             
          }, cleanUpLoader);
    
     }else{
        return; 
     }
  }

  //console.log(`These are the approved qtys:`, approvedQty);
  const handleApprovedStoreReturnClick = (stockReturn: IStockReturn) => {
    setShowApprovedStockReturnModal(true); // open modal 
    setSelectedStockReturnDocNum(stockReturn?.doc_num); //set the doc num
  }

  const handleApprovedStoreReturnSubmit = () => {
    if(selectedStockReturnDocNum !== ""){

     const docData = {
        doc_num: selectedStockReturnDocNum,
        approved: approvedQty, 
        status: "APPROVED",
     };

     fetchData(fetchStockReturnActionsQueryUrl, 
        {method:"POST", body: JSON.stringify(docData)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
              handleBackToReceipts();
              setSelectedStockReturnDocNum(""); //set the doc num
            }
        });
    }else{
        return;
    }
  }

    
  const handleEditClick = (id: string, initialQty: number) => {
    setEditingRowId(id);
    setEditedQty(prev => ({ ...prev, [id]: initialQty }));
    setInputDraft(prev => ({ ...prev, [id]: initialQty.toString() }));
  };

  const handleQtyDraftChange = (id: string, value: string) => {
    setInputDraft(prev => ({ ...prev, [id]: value }));
  };

  const handleBlur = (id: string) => {
      const draftValue = inputDraft[id];
      const parsed = parseInt(draftValue, 10);
      setEditedQty(prev => ({ ...prev, [id]: isNaN(parsed) ? 0 : parsed }));
  };

  const handleSaveClick = (id: string) => {
        const qty = editedQty[id];
        const item_code = stockReturnLineState.find(line => line.id === id)?.item_code;
        const doc_num = selectedStockReturn?.doc_num;

        if (!item_code || qty === undefined || doc_num === undefined) return;

        const payload = {
        doc_num,
        item_code,
        qty_requested: qty,
        mode: 'exact'
        };


        // Perform your save operation here (e.g., API call)
        fetchData(fetchStockReturnLineActionsQueryUrl,
        { method:"POST", body: JSON.stringify(payload)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
                setSubmitRefreshLineData(prev => !prev); 
                setEditingRowId(null); // exit edit mode
                setEditedQty({});
                setInputDraft({});
            }
        }, cleanUpLoader);
    };

    //handles the back to stock returns.
    const handleBackToReceipts = () => {
        setViewMode('default');
        setSelectedStockReturn(null);
        setApprovedQty({});
        setShowCustomQuantitySection(false);
        setSelectedScannedItem(null);
        setCustomQuantity('1');
        setEditingRowId(null); // exit edit mode
        setEditedQty({});
        setInputDraft({});
        fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processData, cleanUpLoader);
    }


    const handleCloseForm = () => {
      setSelectedScannedItem(null);
      setCustomQuantity('1');
      setShowCustomQuantitySection(false); // If you're using this to toggle visibility
    };

    const userRole = userDetail?.roles[0] === "HQ" ? "HQ" : "All";
    const showHQFields = (viewMode === "manager" || viewMode === "view") && userRole === "HQ";
    const showActions = selectedStockReturn?.status === "OPEN";

    // Base columns are always 5
    const baseColSpan = 5;
    const extraCols = showHQFields ? 2 : 0;
    const actionCol = showActions ? 1 : 0;

    const totalColSpan = baseColSpan + extraCols + actionCol;

    const DisabledScanBtn = () => {
        return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Scan"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
             >
             <ScanLine style={{ width: "20px", height: "20px", color: "#6c757d" }} />
           </span>
        ) 
    } 

    const DisabledCheckBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Submit Stock Return"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <Check style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledDeleteBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Set To Delete"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <X style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
       )
    } 


    const DisabledHQAceesBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Manage Approval"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <UserCheck style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledApprovalBtn = () => {
      return (
      <span className="btn h-100 d-flex align-items-center gap-1" title="Approve Return"
            style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
        >
       <ThumbsUp style={{ width: "20px", height: "20px", color: "#6c757d" }} />
      </span>
     )
  } 

    const DisabledPrintBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Print"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <Printer style={{ width: "20px", height: "20px", color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledEditQtyBtn = () => {
              return (
              <span className="btn h-100 d-flex align-items-center gap-1" title="Edit Quantity"
                  style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
              >
              <Pencil style={{ width: "20px", height: "20px", color: "#6c757d" }} />
              </span>
          )
    } 

    const DisabledSaveQtyBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Save Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Save style={{ width: "22px", height: "22px", color: "#6c757d" }} />
        </span>
        )
    } 

    //Handling the file preview
    const handleFilePreview = () => {
      if(selectedFile){

        setPreviewFileUpload(true);
        setPreviewLoading(true);

        // how to do a multipe part form posts javascript  
        const formData = new FormData();
        formData.append('file', selectedFile);

        const headers = new Headers();
        headers.append("Content-Type", "multipart/formdata");

        fetchData(fetchStockTakeFilePreviewQuertUrl, 
        { method:"POST", body: formData, headers: headers }, 
        ((result: any, status: string) =>  { 
          if(status === "200"){
              setPreviewLoading(false);
              setPreviewUploadedFileData(result || []);
              setPreviewFailed(false);
          }else{
              setPreviewUploadedFileData([]);
              setPreviewLoading(false);
              setPreviewFailed(true);
            }
        })); 

      }else{
        return;
      }
  }

  //handling the file preview cancel reset 
  const handleCancelFilePreview = () => {
          setPreviewFileUpload(false);
          setPreviewUploadedFileData([]);
          setStockReturnNotes("");
          setPreviewFailed(false);

          // Reset fileRef after submission
          setSelectedFile(null);
          if (fileRef.current) {
              fileRef.current.value = ''; 
          }
  }



  const handleCloseCreateStockReturnModal = () => {
    setSelectedFile(null);
    if (fileRef.current) {
      fileRef.current.value = '';
    }
    setStockReturnNotes('');
    setShowCreatStockReturnModal(false);
    setPreviewFileUpload(false);
    setPreviewUploadedFileData([]);
    setPreviewFailed(false);
  };

  	

  // let customerLogo = Logo;
  const { src: customerLogo, width: customerLogoWidth } = getCustomerLogo();

  function getCustomerLogo() {
    switch (process.env.REACT_APP_COMPANY) {
      case "ares-bk":
        return { src: bkLogo, width: "150px" };
      case "ares-hd":
        return { src: hdLogo, width: "100px" }; // 80
      case "ares-tb":
        return { src: tbLogo, width: "150px" };
      case "digicape":
        return { src: dcLogo, width: "80px" };
      case "hi-tec":
        return { src: htLogo, width: "80px" };
      case "ares-cr":
        return { src: crLogo, width: "160px" };
      case "ares-ok":
        return { src: okLogo, width: "100px" };
      default:
        return { src: Logo, width: "80px" };
    }
  }

  const physicalAddress = process.env.REACT_APP_ARES_M24L_3PL_ADDRESS;
  const postalCode = process.env.REACT_APP_ARES_M24L_3PL_POSTAL_CODE;
  const cityName = process.env.REACT_APP_ARES_M24L_3PL_CITY;
  const countryName = process.env.REACT_APP_ARES_M24L_3PL_COUNTRY;


  const handlePrint = () => {
       window.print();
   }

  const thStyle = {
      backgroundColor: '#f7f5f5',
      border: '1px solid #deebed',
      fontWeight: 600,
      padding: '10px 8px',
      fontSize: '0.85rem',
  };


  return (
    <>
    { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
     <Card>
            <Card.Header>
                <Card.Title>{`Store Return [${props?.store.name}]`}</Card.Title>                       
            </Card.Header>
            <CardBody>
               <div className="d-flex gap-1">
                    <button
                        className="btn btn-secondary h-100"
                        style={{width: viewMode === 'default' ? "120px" : "160px"}}
                        onClick={() => {
                            if (viewMode === 'default') {
                                props?.close(); 
                            } else {
                                handleBackToReceipts();
                            }
                        }}
                        >
                        { viewMode === 'default' ? "Back to Store" : "Back To Store Return" }
                    </button>
                    {viewMode === 'default' && (
                    <button
                        className="btn btn-secondary h-100"
                        onClick={() => setShowCreatStockReturnModal(true)}
                     >
                       Create Store Return
                     </button>
                    )}
               </div>
               <br></br>                                       
               <div>
                  { /* Stock Return Table Start */ }
                  <table className="table table-bordered">
                            <thead>                           
                                <tr>
                                    <th style={thStyle}>Doc Number</th>
                                    <th style={thStyle}>From Warehouse</th>
                                    <th style={thStyle}>To Warehouse</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Created</th>
                                    <th style={thStyle}>Notes</th>
                                    <th style={{...thStyle, width: '150px'}}>Total Qty Approved</th>
                                    <th style={{...thStyle, width: '150px'}}>Total Qty Requested</th>
                                    <th style={thStyle}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                               { loading && viewMode === "default" ? (
                                 <>
                                    <tr>
                                        <td colSpan={8} className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        </td>
                                    </tr>
                                 </>
                                 ) : (
                                    <>
                                     {!selectedStockReturn && viewMode === "default" && stockReturnState.map((stockReturn: IStockReturn) => (
                                      <tr key={stockReturn.id}>
                                        <td>{stockReturn.doc_num}</td>
                                        <td>{stockReturn.from_whs}</td>
                                        <td>{stockReturn.to_whs}</td>
                                        <td>{stockReturn.status}</td>
                                        <td>{formatDateFull(stockReturn.created)}</td>
                                        <td title={stockReturn?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {stockReturn.notes}
                                            </div>
                                        </td>
                                        <td className="text-end">{stockReturn.total_qty_approved}</td>
                                        <td className="text-end">{stockReturn.total_qty_requested}</td>
                                        <td className="d-flex gap-1">

                                            { stockReturn.status === "OPEN" ? (
                                               <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Scan'
                                                onClick={() => handleScanClick(stockReturn)}
                                                >
                                                  <ScanLine style={{ width: "20px", height: "20px" }} /> 
                                               </button>
                                            ) : (
                                                <DisabledScanBtn/>
                                            )} 

                                           { stockReturn.status === "OPEN" ? (
                                               <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Submit Store Return'
                                                 onClick={() => handlePendingStockReturnClick(stockReturn)}
                                                >
                                                 <Check style={{ width: "20px", height: "20px" }}  /> 
                                               </button>
                                            ) : (
                                                <DisabledCheckBtn/>
                                            )} 

                                            <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='View'
                                              onClick={() => handleViewClick(stockReturn)}
                                            >
                                                <FileText style={{ width: "20px", height: "20px" }}  /> 
                                            </button>

                                            {(stockReturn.status === "OPEN" || (stockReturn.status === "PENDING" && userRole === "HQ")) ? (
                                                 <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Set To Delete'
                                                   onClick={() => handleDeletingStockReturnClick(stockReturn)}
                                                  >
                                                  <X style={{ width: "20px", height: "20px" }}  /> 
                                                 </button>
                                                ) : (
                                                <DisabledDeleteBtn/>
                                            )}  

                                            {userRole === "HQ" && (stockReturn.status === "PENDING" || stockReturn.status === "OPEN" || stockReturn.status === "DELETED") ? (
                                            <button
                                                className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                title="Manage Approval"
                                                onClick={() => handleManagerApprovalViewClick(stockReturn)}
                                            >
                                                <UserCheck style={{ width: "20px", height: "20px" }} />
                                            </button>
                                            ) : (
                                              <DisabledHQAceesBtn />
                                            )}

                                            {stockReturn.status === "COMPLETE" ? (
                                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Print'
                                                 onClick={() => handlePrintStockReturnClick(stockReturn)}
                                                >
                                                <Printer style={{ width: "20px", height: "20px" }}  /> 
                                              </button>
                                            ) : (
                                                  <DisabledPrintBtn />
                                            )}
                                        </td>
                                     </tr>
                                   ))}
                                 </>
                                )}

                                {selectedStockReturn && viewMode !== "default" &&
                                    <>
                                        <tr key={selectedStockReturn.id}>
                                            <td>{selectedStockReturn.doc_num}</td>
                                            <td>{selectedStockReturn.from_whs}</td>
                                            <td>{selectedStockReturn.to_whs}</td>
                                            <td>{selectedStockReturn.status}</td>
                                            <td>{formatDateFull(selectedStockReturn.created)}</td>
                                            <td title={selectedStockReturn?.notes ?? ""}>
                                            <div style={{ 
                                                whiteSpace: "nowrap", 
                                                overflow: "hidden", 
                                                textOverflow: "ellipsis", 
                                                maxWidth: "150px" 
                                            }}>
                                                {selectedStockReturn.notes}
                                            </div>
                                           </td>
                                            <td className="text-end">{selectedStockReturn.total_qty_approved}</td>
                                            <td className="text-end">{selectedStockReturn.total_qty_requested}</td>
                                            <td>
                                            { /* disable if status it deleted */ }
                                                {viewMode === 'manager' && 
                                                 <div className='d-flex gap-1'>
                                                  <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Edit Notes'
                                                  onClick={() => handleOpenEditDocNumNotesClick(selectedStockReturn)}
                                                  >
                                                   <Pencil style={{ width: "20px", height: "20px" }}  /> 
                                                  </button>

                                                  {selectedStockReturn.status === "PENDING" ? (
                                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Approve Return'
                                                    onClick={() => handleApprovedStoreReturnClick(selectedStockReturn)}
                                                    >
                                                    <ThumbsUp style={{ width: "20px", height: "20px" }}  /> 
                                                    </button>
                                                  ) : (
                                                      <DisabledApprovalBtn/>
                                                  )}
                                                 </div>
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                        <td colSpan={9} style={{ textAlign: 'center' }}>
                                          {viewMode === 'view' || viewMode === 'manager' || viewMode === 'print' ? null : (
                                            <>
                                              {selectedScannedItem && showCustomQuantitySection ? (
                                                  <form
                                                    onSubmit={(e) => {
                                                      e.preventDefault();
                                                      handleQuantitySubmit();
                                                    }}
                                                    className="p-3 border rounded bg-light"
                                                    style={{ display: 'inline-block', textAlign: 'left', minWidth: '600px' }}
                                                  >
                                                    <div className="mb-3 position-relative">
                                                      <div className="d-flex justify-content-between align-items-center">
                                                        <h6 className="mb-0" style={{ fontWeight: 600 }}>Scanned Item Details</h6>
                                                        <button
                                                          type="button"
                                                          className="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                          style={{ width: '28px', height: '28px' }}
                                                          onClick={handleCloseForm}
                                                          aria-label="Close"
                                                        >
                                                          <X size={16} />
                                                        </button>
                                                      </div>
                                                      <hr className="my-2" />

                                                      <div className="row">
                                                        <div className="col-md-6 mb-2">
                                                          <label className="form-label mb-0"><strong>Name:</strong></label>
                                                          <div className="form-control-plaintext">{selectedScannedItem.name}</div>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                          <label className="form-label mb-0"><strong>Item Group:</strong></label>
                                                          <div className="form-control-plaintext">{selectedScannedItem.item_group}</div>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                          <label className="form-label mb-0"><strong>Item Code:</strong></label>
                                                          <div className="form-control-plaintext">{selectedScannedItem.item_code}</div>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                          <label className="form-label mb-0"><strong>Barcode:</strong></label>
                                                          <div className="form-control-plaintext">{selectedScannedItem.barcode}</div>
                                                        </div>
                                                      </div>
                                                    </div>

                                                    <div className="d-flex align-items-center justify-content-between">
                                                      <div className="d-flex align-items-center">
                                                        <label htmlFor="custom_quantity" className="form-label mb-0 me-2"><strong>Quantity:</strong></label>
                                                         <input
                                                            type="number"
                                                            id="custom_quantity"
                                                            name="custom_quantity"
                                                            min={1}
                                                            value={customQuantity}
                                                            onChange={(e) => setCustomQuantity(e.target.value)}
                                                            className="form-control form-control-sm"
                                                            style={{ width: '100px' }}
                                                          />
                                                      </div>

                                                      <button type="submit" className="btn btn-dark btn-sm ms-3">
                                                        Save Qauntity
                                                      </button>
                                                    </div>
                                                  </form>
                                              ) : (
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                  <span style={{ marginRight: '10px' }}>Scan</span>
                                                  <input
                                                    ref={scanRef}
                                                    id="scan_code"
                                                    name="scan_code"
                                                    type="text"
                                                    autoComplete="off"
                                                    className="form-control form-control-md w-25"
                                                    style={{ margin: 0 }}
                                                    onKeyDown={(e) => handleScanKeyDown(e, selectedStockReturn.doc_num)}
                                                  />
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </td>
                                      </tr>
                                    </>
                                }       

                            </tbody>
                   </table>
                  { /* Stock Return Table End */ }

                  {selectedStockReturn && viewMode !== "default" && viewMode !== "print" &&
                            <table className="table table-bordered">
                                <thead>                           
                                    <tr>
                                        <th style={thStyle}>Line No.</th>
                                        <th style={thStyle}>Item Name</th>
                                        <th style={thStyle}>Item Code</th>
                                        <th style={thStyle}>Item Barcode</th>
                                        <th style={{...thStyle, width: '120px'}}>Qty Requested</th>
                                         
                                        {showHQFields && (
                                            <>
                                                <th style={{...thStyle, width: '120px'}}>Qty On Hand</th>
                                                <th style={{...thStyle, width: '120px'}}>Qty Approved</th>
                                            </>
                                        )}
                                        {selectedStockReturn?.status === "OPEN" && (
                                            <th style={thStyle}>Actions</th>
                                        )}
                                    </tr>                                                             
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={totalColSpan} className="text-center">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    stockReturnLineState.map((stockReturnLine: IStockReturnLine) => {
                                        const maxQuantity = stockReturnLine.qty_on_hand ?? 0;
                                        const requestedQuantity = stockReturnLine.qty_requested ?? 0;
                                        const approvedValue = approvedQty[stockReturnLine.item_code];

                                        const isEditing = editingRowId === stockReturnLine.id;
                                      
                                        // this is what is shown in the input
                                        const displayQty =
                                          approvedValue !== undefined
                                            ? approvedValue
                                            : Math.min(requestedQuantity, maxQuantity);
                                      
                                        const isRequestedHigher = requestedQuantity > maxQuantity;
                                        const qtyMismatch = displayQty !== maxQuantity;
                                      
                                        return (
                                          <tr key={stockReturnLine.id}>
                                            <td>{stockReturnLine.line_num}</td>
                                            <td>{stockReturnLine.item_name}</td>
                                            <td>{stockReturnLine.item_code}</td>
                                            <td>{stockReturnLine.item_barcode}</td>
                                            <td className="text-end" style={{ position: "relative" }}>
                                              {isEditing ? (
                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    style={{ maxWidth: "80px", fontSize: "0.85rem", padding: "0.25rem" }}
                                                    value={inputDraft[stockReturnLine.id] ?? ''}
                                                    onChange={(e) => handleQtyDraftChange(stockReturnLine.id, e.target.value)}
                                                    onBlur={() => handleBlur(stockReturnLine.id)}
                                                    autoFocus
                                                    onClick={(e) => (e.target as HTMLInputElement).select()}
                                                    onFocus={(e) => (e.target as HTMLInputElement).select()}
                                                    />
                                                  </div>
                                                ) : (
                                                    stockReturnLine.qty_requested
                                                )}

                                                {viewMode === "manager" && isRequestedHigher && (
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      top: 2,
                                                      right: 4,
                                                      backgroundColor: "#f0f0f0",
                                                      borderRadius: "50%",
                                                      padding: "2px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                    }}
                                                  >
                                                    <ArrowUp size={12} color="red" />
                                                  </div>
                                                )}
                                            </td>
                                      
                                            {showHQFields && (
                                              <>
                                                <td>{stockReturnLine.qty_on_hand}</td>
                                                <td style={{ position: "relative", paddingRight:'30px' }}>
                                                  { viewMode === "manager" ? (
                                                <>
                                                  <input
                                                    type="number"
                                                    style={{ width: "100%", }}
                                                    value={displayQty}
                                                    min={0}
                                                    max={maxQuantity}
                                                    onChange={(e) => {
                                                      const newQty = parseInt(e.target.value, 10) || 0;
                                                      if (newQty > maxQuantity) return;
                                                      setApprovedQty((prevState) => ({
                                                        ...prevState,
                                                        [stockReturnLine.item_code]: newQty,
                                                      }));
                                                    }}
                                                  />
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      top: 2,
                                                      right: 4,
                                                      backgroundColor: "#f0f0f0",
                                                      borderRadius: "50%",
                                                      padding: "2px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                    }}
                                                  >
                                                    {qtyMismatch && (<X size={12} color={"orange"} />)}
                                                    
                                                    
                                                  </div>
                                                  </>
                                                    ) : (
                                                        <>{displayQty}</>
                                                    )}
                                                </td>
                                              </>
                                            )}

                                             {selectedStockReturn?.status === "OPEN" && (
                                                  <td className='d-flex gap-1'>
                                                    {isEditing ? (
                                                        <>
                                                          { !inputDraft[stockReturnLine.id]?.trim() ? (
                                                                <DisabledSaveQtyBtn/>
                                                            ) : (
                                                                <button
                                                                className="btn btn-secondary"
                                                                onClick={() => handleSaveClick(stockReturnLine.id)}
                                                                title="Save Quantity"
                                                                >
                                                                <Save style={{ width: "20px", height: "20px" }} />
                                                                </button>
                                                            )
                                                        }
                                                        <button
                                                            className="btn btn-secondary"
                                                            onClick={() => setEditingRowId(null)}
                                                            title="Cancel"
                                                        >
                                                            <X style={{ width: "20px", height: "20px" }} />
                                                        </button>
                                                        </>
                                                    ) : editingRowId === null ? (
                                                        <button
                                                        className="btn btn-secondary"
                                                        onClick={() =>
                                                            handleEditClick(
                                                            stockReturnLine.id,
                                                            stockReturnLine.qty_requested ?? 0
                                                            )
                                                        }
                                                        title="Edit Quantity"
                                                        >
                                                        <Pencil style={{ width: "20px", height: "20px" }} />
                                                        </button>
                                                    ) : (<DisabledEditQtyBtn/>) }
                                              </td>
                                             )}
                                          </tr>
                                        );
                                      })
                                  )}                                                                                                                                                      
                                </tbody>
                            </table>
                        }

                         { selectedStockReturn && viewMode === 'print' && 
                            <div>
                                <h5 className="mt-4 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1 d-flex justify-content-between align-items-center">
                                  <span>Select Print Option:</span>
                                </h5>
                                <div className='d-flex gap-1'>
                                  <button onClick={() => setPrintTarget('label')}  className={`btn ${printTarget === 'label' ? 'btn-primary' : 'btn-secondary'}`}>Label</button>
                                  <button onClick={() => setPrintTarget('manifest')} className={`btn ${printTarget === 'manifest' ? 'btn-primary' : 'btn-secondary'}`}>Manifest</button>
                                </div>
                              </div>
                          }

                            { selectedStockReturn && viewMode === 'print' && (
                            <>
                                <h5 className="mt-4 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1 d-flex justify-content-between align-items-center">
                                <span>{printTarget === 'label' ? 'Print Label' : 'Print Manifest'}</span>
                                <button
                                    className="btn btn-secondary d-flex align-items-center gap-1"
                                    title={printTarget === 'label' ? 'Print Label' : 'Print Manifest'}
                                    onClick={handlePrint}
                                >
                                    <Printer style={{ width: '20px', height: '20px' }} />
                                </button>
                                </h5>
        
                                <div className="preview-print-container">
                                {printTarget === 'label' && (
                                    <div id="print-label" className="print-sticker">
                                    {/* Top Row: Logo + Info */}
                                    <div className="store-logo-section">
                                      {/* Logo */}
                                      <div className='store-logo'>
                                        <img src={customerLogo} style={{ width: customerLogoWidth }} />
                                      </div>

                                      {/* Info */}
                                      <div className='store-ref-section'>
                                        <p style={{ fontWeight: 'bold', margin: 0, fontSize: "18px" }}>Store Return</p>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                          <span style={{ fontWeight: 'bold' }}>Ref:</span>
                                          <span>{selectedStockReturn.doc_num}</span>
                                        </div>
                                      </div>
                                     </div>
        
                                       {/* Warehouse Details */}
                                      <div style={{ marginTop: '10px', marginBottom: '10px', width: '100%' }}>
                                        {[
                                          { label: 'From Warehouse:', value: props?.store.name },
                                          { label: 'To Warehouse:', value: selectedStockReturn.to_whs },
                                          { label: 'Total Items:', value: selectedStockReturn.total_qty_approved },
                                          { label: 'To Warehouse Address:', value: physicalAddress },
                                          { label: 'Postal Code:', value: postalCode },
                                          { label: 'City:', value: cityName },
                                          { label: 'Country:', value: countryName },
                                        ].map(({ label, value }) => (
                                          <div key={label}
                                            style={{ display: 'flex',  gap: '4px', flexWrap: 'wrap', ...(label === 'To Warehouse Address:' ? { marginTop: '12px' } : {}), }}
                                          >
                                            <span style={{ fontWeight: 'bold' }}>{label}</span>
                                            <span>{value}</span>
                                          </div>
                                        ))}
                                      </div>
        
                                        {/* Notes */}
            
                                        {/* Notes */}
                                        <div className="notes-section">
                                          <p style={{ margin: 0 }}>Notes: {selectedStockReturn.notes}</p>
                                        </div>
                                    </div>
                                )}
        
                                {printTarget === 'manifest' && (
                                    <div id="print-label" className="print-sticker">
                                    {/* Manifest content goes here */}
                                    {/* Top Row: Logo + Info */}
                                        <div className="store-logo-section">
                                        {/* Logo */}
                                        <div className='store-logo'>
                                          <img src={customerLogo} style={{ width: customerLogoWidth }} />
                                        </div>

                                        {/* Info */}
                                        <div className='store-ref-section'>
                                          <p style={{ fontWeight: 'bold', margin: 0, fontSize: "18px" }}>Store Return</p>
                                          <div style={{ display: 'flex', gap: '8px' }}>
                                            <span style={{ fontWeight: 'bold' }}>Ref:</span>
                                            <span>{selectedStockReturn.doc_num}</span>
                                          </div>
                                        </div>
                                      </div>
                                       
        
                                       {/* Warehouse Details */}
                                      <div style={{ marginTop: '10px', marginBottom: '10px', width: '100%' }}>
                                        {[
                                          { label: 'From Warehouse:', value: props?.store.name },
                                          { label: 'To Warehouse:', value: selectedStockReturn.to_whs },
                                          { label: 'Total Items:', value: selectedStockReturn.total_qty_approved },
                                          { label: 'To Warehouse Address:', value: physicalAddress },
                                          { label: 'Postal Code:', value: postalCode },
                                          { label: 'City:', value: cityName },
                                          { label: 'Country:', value: countryName },
                                        ].map(({ label, value }) => (
                                          <div key={label}
                                            style={{ display: 'flex',  gap: '4px', flexWrap: 'wrap', ...(label === 'To Warehouse Address:' ? { marginTop: '12px' } : {}), }}
                                          >
                                            <span style={{ fontWeight: 'bold' }}>{label}</span>
                                            <span>{value}</span>
                                          </div>
                                        ))}
                                      </div>
        
        
                                        {/* Adding the Items: label */}
                                    <div style={{ marginTop: '12px', width: '100%' }}>
                                        <span style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Items Manifest:</span>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                                        <thead>
                                            <tr>
                                            <th style={{ textAlign: 'left', padding: '4px', borderBottom: '1px solid #ccc' }}>Code</th>
                                            <th style={{ textAlign: 'left', padding: '4px', borderBottom: '1px solid #ccc' }}>Name</th>
                                            <th style={{ textAlign: 'right', padding: '4px', borderBottom: '1px solid #ccc' }}>Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stockReturnLineState.map((stockReturnLine: IStockReturnLine) => (
                                            <tr key={stockReturnLine.id}>
                                                <td style={{ padding: '4px 8px' }}>{stockReturnLine.item_code}</td>
                                                <td style={{ padding: '4px 8px' }}>{stockReturnLine.item_name}</td>
                                                <td style={{ padding: '4px 8px', textAlign: 'right' }}>{stockReturnLine.qty_approved}</td>
                                            </tr>
                                            ))}
                                        </tbody>
                                        </table>
                                    </div>
        
                                        {/* Notes */}
                                        <div className="notes-section">
                                          <p style={{ margin: 0 }}>Notes: {selectedStockReturn.notes}</p>
                                        </div>
                                    </div>
                                )}
                                </div>
                            </>
                            )}
                          
               </div>
            </CardBody>


                 {/* Modal Create Store Return - Start */}
                 <>
                   <Modal show={showCreatStockReturnModal} onHide={handleCloseCreateStockReturnModal}>
                        <Modal.Header closeButton>
                           <Modal.Title>{`Creating Store Return [${props?.store.name}]`}</Modal.Title>
                          </Modal.Header>
                        <Modal.Body>
                        <div className='informational-message-section'>
                          <strong>Note:</strong> You can proceed without uploading a file. Simply click 
                          <strong> Create </strong> to skip this step and scan your items directly in the interface. 
                          The system will guide you through entering or capturing the necessary information on the next screen.
                        </div>
                        {showErrorAlert && previewFileUpload && <ShowErrorAlert message={responseErrorMessage} /> } 
                         {previewFileUpload ? (
                          <div style={{ width: '100%' }}>
                              <div className="d-flex justify-content-between align-items-center mt-3 mb-2 border-bottom pb-1">
                              <h5 className="text-uppercase text-secondary fw-bolder mb-0">File Preview</h5>
                              {!previewLoading && previewUploadedFileData.length > 0 && (
                                  <div className="fw-bolder text-end text-nowrap" style={{ fontSize: "0.85rem", color: "#333" }}>
                                  Total Qty: {previewUploadedFileData.reduce((acc, cur) => acc + (cur.count || 0), 0)}
                                  </div>
                              )}
                              </div>
                              <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #ddd" }}>
                              <table className="table table-bordered table-sm">
                                  <thead className="table-light">                           
                                      <tr>
                                          <th style={{ color: 'gray' }}>Item Name</th>
                                          <th style={{ color: 'gray' }}>Item Code</th>
                                          <th style={{ color: 'gray' }}>Item Barcode</th>
                                          <th style={{ color: 'gray' }}>Qty</th>
                                      </tr>                                                             
                                  </thead>
                                  <tbody>
                                  {previewLoading ? (
                                      <tr>
                                          <td colSpan={4} className="text-center">
                                              <div className="spinner-border" role="status">
                                                  <span className="visually-hidden">Loading...</span>
                                              </div>
                                          </td>
                                      </tr>
                                  ) : (
                                      previewUploadedFileData.length > 0 && previewUploadedFileData.map((previewLine: IUploadFilePreviewItem) => {
                                          return (
                                          <tr key={previewLine.barcode}>
                                              <td style={{ maxWidth: "120px" }}>
                                                  <span title={previewLine.name ?? ""} style={{ display: "inline-block", maxWidth: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "middle"}}>
                                                   {previewLine.name}
                                                  </span>
                                              </td>
                                              <td>{previewLine.itemCode}</td>
                                              <td>{previewLine.barcode}</td>
                                              <td style={{ textAlign: 'right' }}>{previewLine.count}</td>
                                          </tr>
                                          );
                                      })
                                      )}
                                  </tbody>
                                  </table>
                              </div>
                          </div>
                         ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                <label htmlFor="file_codes" style={{ marginBottom: '6px', fontWeight: 'bold', marginLeft: '4px' }}>
                                   Upload File:
                                  <span style={{ color: '#c0392b', fontWeight: 'normal', marginLeft: '8px' }}>
                                      * Optional – Only .xlsx files supported
                                  </span>
                               </label>
                            <input ref={fileRef} id="file_codes" name="file_codes" type="file" accept=".xlsx" className="form-control form-control-md w-80" onChange={handleFolderChange} />
                            <label htmlFor="file_codes" style={{ marginBottom: '6px', fontWeight: 'bold', marginLeft: '4px', marginTop: '8px' }}>
                                 Leave Notes:
                                <span style={{ color: '#c0392b', fontWeight: 'normal', marginLeft: '8px' }}>
                                    * Optional 
                                </span>
                            </label>
                            <textarea
                                id="store_return_notes"
                                name="store_return_notes"
                                className="form-control form-control-md w-80"
                                value={stockReturnNotes}
                                onChange={(e) => setStockReturnNotes(e.target.value)}
                            />
                        </div>)}
                        </Modal.Body>
                        <Modal.Footer>

                            {/* Main Action Button */}
                            {selectedFile === null || previewFileUpload ? (
                                <Button variant="primary" disabled={(previewFileUpload && previewFailed)}
                                onClick={() => {  setShowCreatStockReturnModal(false); handleCreateStockReturnSubmit(); }}
                                >
                                 { selectedFile ? "Upload" : " Create" } 
                                </Button>
                            ) : (
                                <Button variant="primary" onClick={handleFilePreview}>
                                Preview
                                </Button>
                            )}
        
                            {/* Cancel Button */}
                            <Button variant="secondary" onClick={previewFileUpload ? handleCancelFilePreview : handleCloseCreateStockReturnModal} >
                                Cancel
                            </Button>
                        </Modal.Footer>
                    </Modal>
                 </> 
                {/* Modal Create Store Return - End */}

                  {/* Modal Set Pending Store Return - Start */}
                  <>
                   <Modal show={showPendingStoreReturnModal} onHide={() => { setShowPendingStoreReturnModal(false); }}>
                        <Modal.Header closeButton>
                           <Modal.Title>{`Confirm [${selectedStockReturnDocNum}] As Valid To Process.`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                            <p>Are you sure you would like to Submit this Store Store Return?</p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => {  setShowPendingStoreReturnModal(false); handlePendingStockReturnSubmit(); } }>
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={() => { setShowPendingStoreReturnModal(false); } }>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                 </> 
                {/* Modal Set Pending Store Return  - End */}


                  {/* Modal Delete Store Return - Start */}
                  <>
                   <Modal show={showDeletingStoreReturnModal} onHide={() => { setShowDeletingStoreReturnModal(false); }}>
                        <Modal.Header closeButton>
                           <Modal.Title>{`Set [${selectedStockReturnDocNum}] To Deleted State`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                            <p>Are you sure you would like to delete this store return?</p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { setShowDeletingStoreReturnModal(false);  handleDeleteStockReturnSubmit(); } }>
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={() => { setShowDeletingStoreReturnModal(false); } }>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                 </> 
                {/* Modal Delete Store Return - End */}


                {/* Modal Updated Selected Doc Num Notes - Start */}
                <>
                   <Modal show={showEditDocNumNotesModal} onHide={() => { setShowEditDocNumNotesModal(false); }}>
                        <Modal.Header closeButton>
                           <Modal.Title>{`Update [${selectedStockReturnDocNum}] Notes`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                            <label htmlFor="file_codes" style={{ marginBottom: '6px', fontWeight: 'bold', marginLeft: '4px' }}>
                                Update Notes:
                            </label>
                            <textarea
                                ref={docNumNotesRef}
                                id="doc_num_notes"
                                name="doc_num_notes"
                                className="form-control form-control-md w-80"
                                value={selectedStockReturnDocNumNotes}
                                onChange={(e) => setSelectedStockReturnDocNumNotes(e.target.value)}
                                />
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { setShowEditDocNumNotesModal(false); handleUpdateDocNumNotesSubmit(); } }>
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={() => { setShowEditDocNumNotesModal(false); } }>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                 </> 
                {/* Modal Updated Selected Doc Num Notes - End */}


                  {/* Modal Approve Store Return - Start */}
                  <>
                   <Modal show={showApprovedStockReturnModal} onHide={() => { setShowApprovedStockReturnModal(false); }}>
                        <Modal.Header closeButton>
                           <Modal.Title>{`Approved [${selectedStockReturnDocNum}]`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                            <p>Are you sure you would like to approve this store return?</p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { setShowApprovedStockReturnModal(false); handleApprovedStoreReturnSubmit(); }}>
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={() => { setShowApprovedStockReturnModal(false); } }>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                 </> 
                {/* Modal Approve Store Return - End */}

       </Card>
    </>
   )
}


